import React from 'react';
import { Member } from '../App';

interface MemberDetailsModalProps {
  member: Member | null;
  onClose: () => void;
}

const MemberDetailsModal: React.FC<MemberDetailsModalProps> = ({ member, onClose }) => {
  if (!member) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-2xl font-bold text-gray-900 tracking-tight">Member Profile</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 hover:bg-gray-100 rounded-lg"
          >
            ✕
          </button>
        </div>
        <div className="space-y-6">
          <div className="text-center">
            <div className="relative inline-block">
              {member.photoUrl ? (
                <img src={member.photoUrl} alt="Profile" className="h-20 w-20 rounded-full mx-auto mb-4 shadow-sm object-cover" />
              ) : (
                <div className="h-20 w-20 rounded-full bg-gray-900 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 shadow-sm">
                  {member.name.charAt(0)}
                </div>
              )}
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">{member.name}</h4>
            <div className="text-gray-500 text-sm font-medium mb-2">{member.gender}</div>
          </div>
          <div className="border-t border-gray-100 pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Email Address</label>
                <p className="text-gray-900 font-semibold">{member.email}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Contact Number</label>
                <p className="text-gray-900 font-semibold">{member.phone}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Membership Type</label>
                <p className="text-gray-900 font-semibold">{member.membershipType}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Plan Selected</label>
                <p className="text-gray-900 font-semibold">{member.plan}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Joining Date</label>
                <p className="text-gray-900 font-semibold">{new Date(member.joinDate).toLocaleDateString()}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Expiry Date</label>
                <p className="text-gray-900 font-semibold">{new Date(member.expiryDate).toLocaleDateString()}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Payment Method</label>
                <p className="text-gray-900 font-semibold">{member.paymentMethod}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Payment Status</label>
                <p className="text-gray-900 font-semibold">{member.paymentStatus}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Invoice/Receipt Number</label>
                <p className="text-gray-900 font-semibold">{member.invoiceNumber}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Height (cm)</label>
                <p className="text-gray-900 font-semibold">{member.height}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Weight (kg)</label>
                <p className="text-gray-900 font-semibold">{member.weight}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 md:col-span-2">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Medical Conditions</label>
                <p className="text-gray-900 font-semibold">{member.medical}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 md:col-span-2">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Fitness Goals</label>
                <p className="text-gray-900 font-semibold">{member.fitnessGoal}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Emergency Contact Name</label>
                <p className="text-gray-900 font-semibold">{member.emergencyName}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Emergency Contact Number</label>
                <p className="text-gray-900 font-semibold">{member.emergencyNumber}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 md:col-span-2">
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">Legal & Consent</label>
                <ul className="text-gray-900 font-semibold list-disc ml-5">
                  <li>Gym Rules & Waiver Agreement: {member.rulesAgreement ? 'Accepted' : 'Not Accepted'}</li>
                  <li>Terms & Conditions: {member.termsAgreement ? 'Accepted' : 'Not Accepted'}</li>
                </ul>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors duration-200 font-semibold mt-8 border border-gray-200"
          >
            Close Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemberDetailsModal;
